import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import type { ForwardedRef } from "react";
import { forwardRef, useEffect, useState } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import FormInput from "~/components/formElements/FormInput";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import { InputPlain } from "~/components/formElements/Input.tsx";
import Label from "~/components/formElements/Label/Label";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";

type SharedProps =
	Omit<React.InputHTMLAttributes<HTMLInputElement>, "name" | "onChange" | "onBlur">
	& FormInputBaseVariants
	& {
	errorIconOnly?: boolean;
	name: string;
	label?: string;
	error?: string;
	minValue?: number;
	maxValue?: number;
}

type CurrencyInputProps = SharedProps & {
	value: number;
	onChange: (value: number) => void;
	onBlur?: () => void;
}

export const CurrencyInputPlain = forwardRef<HTMLInputElement, CurrencyInputProps>(
	({
		disabled,
		error,
		errorIconOnly,
		label,
		minValue,
		maxValue,
		name,
		onBlur,
		onChange,
		value,
		...props
	}: CurrencyInputProps, ref: ForwardedRef<HTMLInputElement>) => {
		const [displayValue, setDisplayValue] = useState<string>(formatNumberToCurrency(value));

		useEffect(() => {
			setDisplayValue(formatNumberToCurrency(value));
		}, [value]);

		function clamp(val: number): number {
			if (minValue !== undefined && val < minValue) return minValue;
			if (maxValue !== undefined && val > maxValue) return maxValue;
			return val;
		}

		function handleKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
			if (e.key === "ArrowUp" || e.key === "ArrowDown") {
				e.preventDefault();
				const incrementValue = e.shiftKey ? 1000 : 100;
				let numberValue = value;
				if (e.key === "ArrowUp") {
					numberValue += incrementValue;
				} else {
					numberValue -= incrementValue;
				}
				onChange(clamp(numberValue));
			}
		}

		function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
			e.target.select();
		}

		function intelligentParse(input: string): number {
			const isNegative = input.trimStart().startsWith("-");

			// Remove all non-numeric characters except commas and dots
			let cleaned = input.replace(/[^\d,.]*/g, "");

			// Remove leading zeros, but keep at least one digit if it's all zeros
			cleaned = cleaned.replace(/^0+(?=\d)/, "");

			// Find the last separator (comma or dot)
			const lastSeparatorIndex = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));

			let integerPart: string;
			let decimalPart: string = "00";

			if (lastSeparatorIndex !== -1) {
				integerPart = cleaned.slice(0, lastSeparatorIndex).replace(/[,.]*/g, "");
				decimalPart = cleaned.slice(lastSeparatorIndex + 1).padEnd(2, "0").slice(0, 2);
			} else {
				integerPart = cleaned;
			}

			const result = parseInt(integerPart + decimalPart, 10) || 0;
			return isNegative ? -result : result;
		}

		function handleBlur() {
			let numberValue = intelligentParse(displayValue);
			numberValue = clamp(numberValue);
			onChange(numberValue);
			setDisplayValue(formatNumberToCurrency(numberValue));
			onBlur && onBlur();
		}

		function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
			setDisplayValue(e.target.value);
		}

		return (
			<FormInput>
				{label && <Label htmlFor={name}
								 disabled={disabled}>{label}</Label>}
				<div className="relative rounded-md shadow-sm">
					<InputPlain
						disabled={disabled}
						name={name}
						max={maxValue}
						type="text"
						value={displayValue}
						onChange={handleChange}
						onBlur={handleBlur}
						onFocus={handleFocus}
						onKeyDown={handleKeydown}
						ref={ref}
						{...props}
					/>
					{error && (
						<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
							<ExclamationCircleIcon className="size-5 text-red-500"
												   aria-hidden="true" />
						</div>
					)}
				</div>
				{error && !errorIconOnly && (
					<p className="mt-2 text-sm text-red-600"
					   id="email-error">
						{error}
					</p>
				)}
			</FormInput>
		);
	},
);

CurrencyInputPlain.displayName = "CurrencyInputPlain";

type CurrencyInputControllerProps = SharedProps & {
	name: string;
	control: Control<any>;
	defaultValue?: number;
	useControllerError?: boolean;
	rules?: any;
}

const CurrencyInput: React.FC<CurrencyInputControllerProps> = ({
	name,
	control,
	minValue,
	maxValue,
	useControllerError,
	error,
	rules,
	...rest
}) => {
	const { field, fieldState: { error: errorFromRHF } } = useController({
		name,
		control,
		rules,
	});

	const errorMessage = useControllerError ? errorFromRHF?.message : error;
	return <CurrencyInputPlain {...field} {...rest} error={errorMessage}
							   minValue={minValue}
							   maxValue={maxValue} />;
};

export default CurrencyInput;